import type { AxiosInstance } from 'axios';
import * as xform from './transformers';
import { FileUploadStatus } from '../enums';
import type { FileUploads as WS } from './interfaces/workspaces';

export class WorkspacesFileUploadClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  queueFileUpload = (data: WS.QueueFileUpload.Request) => {
    const { workspaceId, ...body } = data;
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads`, body)
      .then(res => res.data)
      .then((res: WS.QueueFileUpload.Response) => ({
        ...res,
        fileUpload: xform.normalizeFileUpload(res.fileUpload),
      }));
  };

  startFileUpload = (data: WS.StartFileUpload.Request) => {
    const { fileUploadId, workspaceId, ...body } = data;
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads/${fileUploadId}`, {
      ...body,
      status: FileUploadStatus.InProgress,
    })
      .then(res => res.data)
      .then((res: WS.StartFileUpload.Response) => ({
        ...res,
        credentials: {
          ...res.credentials,
          expiration: res.credentials.expiration
            ? new Date(res.credentials.expiration)
            : res.credentials.expiration,
        },
        fileUpload: xform.normalizeFileUpload(res.fileUpload),
      }));
  };

  abortFileUpload = (data: WS.AbortFileUpload.Request) => {
    const { fileUploadId, workspaceId, ...body } = data;
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads/${fileUploadId}`, {
      ...body,
      status: FileUploadStatus.Aborted,
    })
      .then(res => res.data)
      .then((res: WS.AbortFileUpload.Response) => ({
        ...res,
        fileUpload: xform.normalizeFileUpload(res.fileUpload),
      }));
  };

  failFileUpload = (data: WS.FailFileUpload.Request) => {
    const { fileUploadId, workspaceId, ...body } = data;
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads/${fileUploadId}`, {
      ...body,
      status: FileUploadStatus.Failed,
    })
      .then(res => res.data)
      .then((res: WS.FailFileUpload.Response) => ({
        ...res,
        fileUpload: xform.normalizeFileUpload(res.fileUpload),
      }));
  };

  completeFileUpload = (data: WS.CompleteFileUpload.Request) => {
    const { fileUploadId, workspaceId, ...body } = data;
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads/${fileUploadId}`, {
      ...body,
      status: FileUploadStatus.Completed,
    })
      .then(res => res.data)
      .then((res: WS.CompleteFileUpload.Response) => ({
        ...res,
        file: xform.normalizeFile(res.file)!,
        fileUpload: xform.normalizeFileUpload(res.fileUpload)!,
        object: xform.normalizeWorkspaceObject(res.object)!,
      }));
  };

  fileUploadPreCheck = ({ workspaceId, ...data }: { workspaceId: number } & Record<string, unknown>) => {
    return this.authenticatedClient.post(`/workspaces/${workspaceId}/file-uploads/pre-check`, data).then(res => res.data);
  };
}

export default WorkspacesFileUploadClient;